import React, { useEffect, useState } from "react";
import { AiOutlineArrowRight } from "react-icons/ai";
import { useCurrency } from "../../context/CurrencyContext";
import { Link } from "react-router-dom";
import { FiPackage, FiShoppingBag, FiTrendingUp, FiDollarSign } from "react-icons/fi";
import {
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineExclamation,
  HiOutlineArrowRight,
} from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfShop } from "../../redux/actions/order";
import { getAllProductsShop } from "../../redux/actions/product";
import { getAllEventsShop } from "../../redux/actions/event";
import { getOrderNumber } from "../../utils/orderUtils";

const DashboardHero = () => {
  const dispatch = useDispatch();
  const { orders } = useSelector((state) => state.order);
  const { seller } = useSelector((state) => state.seller);
  const { products } = useSelector((state) => state.products);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    if (seller?._id) {
      dispatch(getAllOrdersOfShop(seller._id));
      dispatch(getAllProductsShop(seller._id));
      dispatch(getAllEventsShop(seller._id));
    }
  }, [dispatch, seller]);

  const availableBalance = seller?.availableBalance?.toFixed(2) || "0.00";
  const totalOrders = orders?.length || 0;
  const totalProducts = products?.length || 0;
  const deliveredOrders = orders?.filter((o) => o.status === "Delivered").length || 0;
  const pendingOrders = orders?.filter((o) => o.status !== "Delivered").length || 0;
  const totalRevenue = orders?.reduce((acc, o) => acc + (o.totalPrice || 0), 0).toFixed(2) || "0.00";

  const recentOrders = orders ? [...orders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5) : [];

  const StatCard = ({ title, value, icon: Icon, accent = false }) => (
    <div className={`border ${accent ? "bg-black border-black text-white" : "bg-white border-secondary-200 text-black"} p-5 transition-all hover:shadow-moc-md`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-[10px] uppercase tracking-widest font-semibold mb-2 ${accent ? "text-accent-400" : "text-secondary-400"}`}>{title}</p>
          <p className={`text-2xl font-bold ${accent ? "text-accent-500" : "text-black"}`}>{value}</p>
        </div>
        <div className={`p-2 ${accent ? "bg-white/10" : "bg-secondary-100"}`}>
          <Icon className={`w-5 h-5 ${accent ? "text-accent-500" : "text-secondary-500"}`} />
        </div>
      </div>
    </div>
  );

  const getStatusStyle = (status) => {
    if (status === "Delivered") return "bg-green-50 text-green-700 border-green-200";
    if (status === "Processing") return "bg-blue-50 text-blue-700 border-blue-200";
    if (status === "Shipped") return "bg-purple-50 text-purple-700 border-purple-200";
    return "bg-yellow-50 text-yellow-700 border-yellow-200";
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Page header */}
      <div className="bg-black px-6 py-8 mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-accent-500 mb-2">Vendor Dashboard</p>
        <h1 className="text-2xl font-bold text-white mb-1">Welcome back, {seller?.name || "Vendor"}</h1>
        <p className="text-secondary-400 text-sm">Here is what is happening with your store today.</p>
      </div>

      <div className="px-6 pb-10 space-y-8">
        {/* Stats */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold mb-4">Overview</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Total Revenue" value={formatPrice(totalRevenue)} icon={FiDollarSign} accent />
            <StatCard title="Total Orders" value={totalOrders} icon={FiShoppingBag} />
            <StatCard title="Products Live" value={totalProducts} icon={FiPackage} />
            <StatCard title="Balance" value={formatPrice(availableBalance)} icon={FiTrendingUp} />
          </div>
        </div>

        {/* Order stats row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-secondary-200 p-5">
            <div className="flex items-center gap-2 mb-1">
              <HiOutlineCheckCircle className="w-4 h-4 text-green-500" />
              <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold">Delivered</p>
            </div>
            <p className="text-2xl font-bold text-black">{deliveredOrders}</p>
          </div>
          <div className="bg-white border border-secondary-200 p-5">
            <div className="flex items-center gap-2 mb-1">
              <HiOutlineClock className="w-4 h-4 text-yellow-500" />
              <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold">Pending</p>
            </div>
            <p className="text-2xl font-bold text-black">{pendingOrders}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold mb-4">Quick Actions</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: "Add New Product", desc: "List a new item for sale", link: "/dashboard-create-product", icon: FiPackage },
              { title: "View All Orders", desc: "Manage incoming orders", link: "/dashboard-orders", icon: FiShoppingBag },
              { title: "Vendor Payments", desc: "Track your earnings", link: "/dashboard-vendor-payments", icon: FiDollarSign },
            ].map((action, i) => (
              <Link key={i} to={action.link} className="group bg-white border border-secondary-200 hover:border-black p-5 transition-all hover:shadow-moc-md flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-secondary-100 group-hover:bg-black flex items-center justify-center transition-colors">
                    <action.icon className="w-5 h-5 text-secondary-500 group-hover:text-accent-500 transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-black">{action.title}</p>
                    <p className="text-xs text-secondary-400 mt-0.5">{action.desc}</p>
                  </div>
                </div>
                <HiOutlineArrowRight className="w-4 h-4 text-secondary-300 group-hover:text-black transition-colors" />
              </Link>
            ))}
          </div>
        </div>

        {/* Vendor Workflow Banner */}
        {seller?.approvalStatus === "pending" && (
          <div className="bg-accent-50 border border-accent-200 p-5">
            <div className="flex items-start gap-3">
              <HiOutlineExclamation className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-black mb-1">Application Under Review</p>
                <p className="text-xs text-secondary-600">Your vendor application is being reviewed by our admin team. You will be notified once approved. Check your email for updates.</p>
              </div>
            </div>
          </div>
        )}

        {/* Recent Orders */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold">Recent Orders</p>
            <Link to="/dashboard-orders" className="text-xs font-semibold text-black hover:text-accent-600 transition-colors uppercase tracking-wider flex items-center gap-1">
              View All <AiOutlineArrowRight size={12} />
            </Link>
          </div>

          {recentOrders.length === 0 ? (
            <div className="bg-white border border-secondary-200 p-10 text-center">
              <FiShoppingBag className="w-10 h-10 text-secondary-200 mx-auto mb-3" />
              <p className="text-sm text-secondary-400">No orders yet</p>
              <p className="text-xs text-secondary-300 mt-1">Orders will appear here once customers purchase your products</p>
            </div>
          ) : (
            <div className="bg-white border border-secondary-200 divide-y divide-secondary-100">
              {/* Table header */}
              <div className="hidden sm:grid grid-cols-4 gap-4 px-5 py-3 bg-secondary-50">
                <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold">Order ID</p>
                <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold">Items</p>
                <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold">Total</p>
                <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold">Status</p>
              </div>
              {recentOrders.map((order) => (
                <div key={order._id} className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 items-center hover:bg-secondary-50 transition-colors">
                  <p className="text-xs font-mono text-black font-semibold truncate">{getOrderNumber(order)}</p>
                  <p className="text-xs text-secondary-600 sm:hidden">
                    <span className="font-semibold text-black">{order.cart?.reduce((a, i) => a + i.qty, 0) || 0}</span> items
                  </p>
                  <p className="text-xs text-secondary-600 hidden sm:block">{order.cart?.reduce((a, i) => a + i.qty, 0) || 0} items</p>
                  <p className="text-xs font-bold text-black hidden sm:block">{formatPrice(order.totalPrice)}</p>
                  <div className="flex items-center justify-end sm:justify-start gap-3">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${getStatusStyle(order.status)}`}>
                      {order.status}
                    </span>
                    <Link to={`/order/${order._id}`} className="text-[10px] font-semibold text-black hover:text-accent-600 uppercase tracking-wider flex items-center gap-1 whitespace-nowrap">
                      View <AiOutlineArrowRight size={10} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Vendor workflow steps */}
        <div className="bg-black p-6">
          <p className="text-[10px] uppercase tracking-widest text-accent-500 font-semibold mb-4">Vendor Workflow</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { step: 1, label: "Register & Apply" },
              { step: 2, label: "Admin Approval" },
              { step: 3, label: "Upload Products" },
              { step: 4, label: "Customer Orders" },
              { step: 5, label: "Ship & Deliver" },
              { step: 6, label: "Payment Processed" },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-8 h-8 bg-accent-500 flex items-center justify-center mx-auto mb-2">
                  <span className="text-xs font-bold text-black">{s.step}</span>
                </div>
                <p className="text-[10px] text-secondary-400 uppercase tracking-wider leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHero;
